import { Box, Grid2, Skeleton, styled, Typography } from "@mui/material";
import PurpleBlinkingLottie from "../../Components/Lotties/PurpleBlinkingLottie.json";
import Lottie from "react-lottie";
import { StyledCard } from "./components/StyledComponents";
import CustomIcon from "../../Components/CustomIcon";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { PrimaryTooltip } from "../../Components/StyledComponent";
import { getInventoryFilterUrl } from "./CommonFunctions";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { BATTERY_STATUS_MAP, PING_STATUS_MAP, TAG_BATTERY_STATUS_MAP } from "../../Helper/DigitalTwin/DataFile";
import { useTranslation } from "react-i18next";
import { useBaseEntityInfo } from "../../Hooks/usePagesContent";
import VehicleStatusInfoTooltip from "../../Components/VehicleStatusInfoTooltip";
import { getLiveStatusScopeNote } from "../../Helper/Overview/totalVehicles";

// Always one row of four. Below LIVE_STATUS_WIDE (the box area, container "liveStatus") the boxes go
// compact: icon above an 11px label, smaller count, so DISCHARGING fits without wrapping at 1280.
const LIVE_STATUS_WIDE = "@container liveStatus (min-width: 610px)";
// At the narrowest (about a 1280 screen) the arrow goes so a 5-digit count still fits; the box stays a link.
const LIVE_STATUS_NARROWEST = "@container liveStatus (max-width: 479px)";

const StatusGrid = styled(Box)(() => ({
  flex: 1,
  display: "grid",
  gap: "8px",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
}));

const StatusCard = styled(Box)(({ item }) => ({
  backgroundColor: "#F9F9F9",
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "flex-start",
  minWidth: 0,
  padding: "12px 8px",
  gap: "8px",
  cursor: item.filterUrl ? "pointer" : "default",
  "& .status-label": { flexDirection: "column", alignItems: "flex-start", gap: "2px" },
  "& .status-label-text": { fontSize: "11px", lineHeight: "16px" },
  "& .status-label svg": { width: 16, height: 16 },
  "& .status-value-row": { width: "100%" },
  "& .status-value": { fontSize: "24px", lineHeight: "28px", marginLeft: 0 },
  "& .chevron-icon": { fontSize: "24px" },
  [LIVE_STATUS_NARROWEST]: {
    "& .chevron-icon": { display: "none" },
  },
  [LIVE_STATUS_WIDE]: {
    padding: "16px",
    "& .status-label": { flexDirection: "row", alignItems: "center", gap: "4px" },
    "& .status-label-text": { fontSize: "14px", lineHeight: "24px" },
    "& .status-label svg": { width: 18, height: 18 },
    "& .status-value": { fontSize: "28px", marginLeft: "8px" },
    "& .chevron-icon": { fontSize: "35px" },
  },
  ...(item.filterUrl && {
    "&:hover": {
      backgroundColor: "#F7F7FF",
    },
    "&:hover .chevron-icon": {
      color: "#4D44D8",
    },
  }),
}));

const LiveVehicleStatusCard = ({ stats, globalFilters, loading }) => {
  const { baseEntityTypeLable, baseEntityTypePluralLable } = useBaseEntityInfo();
  const scopeNote = getLiveStatusScopeNote(
    baseEntityTypePluralLable,
    stats?.totalVehicles?.hasOnboardingStatus
  );
  const {
    Charging: chargingCount,
    Discharging: dischargingCount,
    Idle: idleCount,
    Offline: offlineCount,
  } = stats;

  const lottieOptions = {
    loop: true,
    autoplay: true,
    animationData: PurpleBlinkingLottie,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  const [cards, setCards] = useState([]);

  useEffect(() => {
    const cardConfigs = [
      {
        label: "CHARGING",
        value: chargingCount ?? "-",
        icon: (
          <CustomIcon
            name="charge-icon"
            sx={{ color: "#008A15", width: 18, height: 18 }}
          />
        ),
        filterUrl:
          typeof chargingCount === "number"
            ? getInventoryFilterUrl(globalFilters, { batteryStatus: [BATTERY_STATUS_MAP.CHARGING], pingStatus: [PING_STATUS_MAP.ACTIVE], tag_battery_status: [TAG_BATTERY_STATUS_MAP.OPERATIONAL, TAG_BATTERY_STATUS_MAP.NON_OPERATIONAL] })
            : undefined,
      },
      {
        label: "DISCHARGING",
        value: dischargingCount ?? "-",
        icon: (
          <CustomIcon
            name="discharge-icon"
            sx={{ color: "#DCA101", width: 18, height: 18 }}
          />
        ),
        filterUrl:
          typeof dischargingCount === "number"
            ? getInventoryFilterUrl(globalFilters, { batteryStatus: [BATTERY_STATUS_MAP.DISCHARGING], pingStatus: [PING_STATUS_MAP.ACTIVE], tag_battery_status: [TAG_BATTERY_STATUS_MAP.OPERATIONAL, TAG_BATTERY_STATUS_MAP.NON_OPERATIONAL] })
            : undefined,
      },
      {
        label: "IDLE",
        value: idleCount ?? "-",
        icon: <CustomIcon name="pause-icon" sx={{ width: 18, height: 18 }} />,
        filterUrl:
          typeof idleCount === "number"
            ? getInventoryFilterUrl(globalFilters, { batteryStatus: [BATTERY_STATUS_MAP.IDLE], pingStatus: [PING_STATUS_MAP.ACTIVE], tag_battery_status: [TAG_BATTERY_STATUS_MAP.OPERATIONAL, TAG_BATTERY_STATUS_MAP.NON_OPERATIONAL] })
            : undefined,
      },
      {
        label: "OFFLINE",
        value: offlineCount ?? "-",
        icon: <FiberManualRecordIcon sx={{ color: "#8F8F8F" }} />,
        filterUrl:
          typeof offlineCount === "number"
            ? getInventoryFilterUrl(globalFilters, { pingStatus: [PING_STATUS_MAP.INACTIVE], tag_battery_status: [TAG_BATTERY_STATUS_MAP.OPERATIONAL, TAG_BATTERY_STATUS_MAP.NON_OPERATIONAL] }, { sortBy: 'lastGPSPing', orderBy: 'asc' })
            : undefined,
      },
    ];
    setCards(cardConfigs);
  }, [globalFilters, stats]);

  return (
    <StyledCard sx={{ boxSizing: "border-box" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          height: "100%",
          containerType: "inline-size",
          containerName: "liveStatus",
        }}
      >
        <Grid2 container gap={1}>
          <Box>
            <Lottie options={lottieOptions} height={20} width={20} />
          </Box>
          <Typography variant="fw500s16lh20c000000">
            Live {baseEntityTypeLable} Status
          </Typography>
          <VehicleStatusInfoTooltip footnote={scopeNote} />
        </Grid2>
        {/* Grows to the row height so the boxes fill the card instead of leaving a gap under them. */}
        <StatusGrid>
          {cards.map((item) => {
            const Wrapper = item.filterUrl ? Link : Box;
            return (
              <Wrapper
                key={item.label}
                {...(item.filterUrl
                  ? {
                    to: item.filterUrl,
                    target: "_blank",
                    style: { display: "contents" },
                  }
                  : { style: { display: "contents", pointerEvents: "none" } })}
              >
                <PrimaryTooltip
                  title={item.filterUrl ? "View in Inventory" : ""}
                >
                  <StatusCard item={item}>
                    <Box
                      className="status-label"
                      sx={{ display: "flex", minWidth: 0, whiteSpace: "nowrap" }}
                    >
                      {item.icon}
                      <Typography
                        className="status-label-text"
                        variant="fw500s14c494949"
                      >
                        {item.label}
                      </Typography>
                    </Box>
                    <Box
                      className="status-value-row"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      {loading ? (
                        <Skeleton className="status-value" variant="text" width={48} height={28} />
                      ) : (
                        <Typography className="status-value" variant="fw500s20c000000">
                          {item.value}
                        </Typography>
                      )}
                      {item.filterUrl && (
                        <ChevronRightIcon
                          className="chevron-icon"
                          fontSize="large"
                          sx={{ color: "#8F8F8F" }}
                        />
                      )}
                    </Box>
                  </StatusCard>
                </PrimaryTooltip>
              </Wrapper>
            );
          })}
        </StatusGrid>
      </Box>
    </StyledCard>
  );
};

export default LiveVehicleStatusCard;
